import { Hash, Inbox, Circle, Square, Triangle, Diamond, Check, X } from 'lucide-react';
import { getAnswerConfig } from '../../utils/answerConfig';

const LUCIDE_BY_NAME = {
  circle: Circle,
  square: Square,
  triangle: Triangle,
  diamond: Diamond,
  check: Check,
  x: X,
};

export default function QuestionDisplay({ question, questionNumber, totalQuestions, answerCount, sessionCode, timeProgress }) {
  if (!question) {
    return null;
  }

  const answerConfig = getAnswerConfig(question.type);
  const isTwoAnswers = question.answers.length <= 2;

  return (
    <div className="bg-mp-bg-lightgreen font-body text-mp-darkgreen h-screen w-screen overflow-hidden flex flex-col">
      {/* Progress Bar */}
      <div className="h-2 w-full bg-mp-darkgreen/10 shrink-0">
        <div
          className="h-full bg-mp-lime rounded-r-full transition-all duration-1000"
          style={{ width: `${timeProgress || 65}%` }}
        />
      </div>

      <div className="flex-1 flex flex-col w-full overflow-hidden">
        {/* Question Card — single line, compact */}
        <div className="shrink-0 w-full px-4 pt-3 pb-2">
          <div className="w-full bg-white rounded-2xl shadow-mp-2 px-5 py-3 mx-auto max-w-4xl flex items-center gap-3 justify-center flex-wrap border-l-[6px] border-mp-darkgreen">
            <span className="shrink-0 font-display text-[11px] text-mp-teal uppercase tracking-[0.18em] tabular-nums">
              {questionNumber} / {totalQuestions}
            </span>
            <span aria-hidden className="text-mp-darkgreen/20">·</span>
            <h1 className="font-display text-xl md:text-3xl text-mp-darkgreen text-center leading-tight">
              {question.text}
            </h1>
          </div>
        </div>

        {/* Image Area (full image visible, never cropped — fills available space) */}
        <div className="flex-1 min-h-0 w-full bg-mp-darkgreen p-2 relative">
          {question.imageUrl && (
            <div
              className="w-full h-full bg-contain bg-center bg-no-repeat rounded-xl"
              style={{ backgroundImage: `url("${question.imageUrl}")` }}
              role="img"
              aria-label=""
            />
          )}
          {/* Answer Count Badge — overlay on image area */}
          {answerCount !== undefined && (
            <div className="absolute top-4 right-4 inline-flex items-center gap-2.5 bg-mp-darkgreen text-white px-4 py-2 rounded-full shadow-mp-2">
              <Inbox size={16} className="text-mp-lime" />
              <span className="font-display text-xl text-mp-lime tabular-nums leading-none">{answerCount}</span>
              <span className="font-display text-[10px] uppercase tracking-[0.18em] text-white/70">Svar</span>
            </div>
          )}
        </div>

        {/* Answer Grid */}
        <div className="shrink-0 bg-white p-2 pb-safe flex flex-col gap-2 z-30">
          <div className={`grid grid-cols-2 gap-2 ${isTwoAnswers ? 'h-[18vh] min-h-[140px]' : 'grid-rows-2 h-[24vh] min-h-[180px]'}`}>
            {question.answers.map((answer, index) => {
              const config = answerConfig[index];
              if (!config) return null;
              const LucideIcon = LUCIDE_BY_NAME[config.lucideName] || Circle;
              return (
                <div
                  key={answer.id || index}
                  className={`relative ${config.color} ${config.textColor} rounded-2xl flex flex-col items-center justify-center p-4 shadow-mp-2 group overflow-hidden`}
                >
                  <div className="absolute top-3 left-3 opacity-60">
                    <LucideIcon size={24} strokeWidth={2.5} />
                  </div>
                  <span className="font-display text-lg md:text-2xl text-center leading-tight z-10">
                    {answer.text}
                  </span>
                  <LucideIcon
                    size={120}
                    strokeWidth={1.5}
                    className="absolute -bottom-6 -right-4 opacity-15 pointer-events-none"
                  />
                </div>
              );
            })}
          </div>

          {/* Footer */}
          {sessionCode && (
            <div className="flex justify-between items-center px-2 py-1.5">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-mp-bg-lightgreen rounded-full">
                <Hash size={14} className="text-mp-teal" />
                <span className="font-display text-xs text-mp-darkgreen tracking-[0.16em]">PIN: {sessionCode}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
